import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { MessageCircle, Send, Search, User, ArrowLeft, RefreshCw, AlertCircle, MapPin, FileText, Download, Image as ImageIcon, Link, Unlink, Building, Phone, Tag, Star, X, UserCheck, BookOpen, StickyNote, Plus, Check, Clock, CircleDot, Filter, ChevronDown, Sparkles, Loader2, Pin } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useLineChatStore } from '../stores/lineChatStore'
import type { LineMessage, LineConversation } from '../stores/lineChatStore'
import type { Customer } from '../types/customer'
import { useVirtualScroll } from '../hooks/useVirtualScroll'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'

interface LineTemplate {
  organizationId: string
  templateId: string
  title: string
  content: string
  category?: string
  createdAt: string
}

interface Operator {
  userId: string
  displayName?: string
  name?: string
  avatar?: string
  role?: string
}

// API レスポンス型
interface ConversationsResponse {
  conversations: LineConversation[]
  nextCursor?: string
}

interface MessagesResponse {
  messages: LineMessage[]
  nextCursor?: string
}

interface CustomerResponse {
  customer: Customer | null
}

interface CustomerSearchResponse {
  customers?: Customer[]
  items?: Customer[]
}

interface UsersResponse {
  items: Operator[]
}

interface TemplatesResponse {
  templates: LineTemplate[]
}

interface AiConfigResponse {
  aiConfig?: { apiKey?: string }
}

interface AiGenerateResponse {
  generatedText: string
}

interface SendMessageResponse {
  messageId: string
  createdAt: string
}

interface ImageUploadResponse {
  messageId: string
  mediaUrl: string
  createdAt: string
}

/**
 * LINE チャットページ
 * - 左: 友だち（トーク）一覧
 * - 右: メッセージ表示 + 返信入力
 */
export default function LineChatPage() {
  const api = useApi()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialLineUserIdRef = useRef(searchParams.get('lineUserId'))

  // Store state
  const conversations = useLineChatStore(s => s.conversations)
  const selectedLineUserId = useLineChatStore(s => s.selectedLineUserId)
  const messages = useLineChatStore(s => s.messages)
  const conversationsLoading = useLineChatStore(s => s.conversationsLoading)
  const messagesLoading = useLineChatStore(s => s.messagesLoading)

  // Store actions
  const setConversations = useLineChatStore(s => s.setConversations)
  const appendConversations = useLineChatStore(s => s.appendConversations)
  const setConversationsNextCursor = useLineChatStore(s => s.setConversationsNextCursor)
  const conversationsNextCursor = useLineChatStore(s => s.conversationsNextCursor)
  const conversationsLoadingMore = useLineChatStore(s => s.conversationsLoadingMore)
  const setConversationsLoadingMore = useLineChatStore(s => s.setConversationsLoadingMore)
  const setSelectedLineUserId = useLineChatStore(s => s.setSelectedLineUserId)
  const setMessages = useLineChatStore(s => s.setMessages)
  const prependMessages = useLineChatStore(s => s.prependMessages)
  const setMessagesNextCursor = useLineChatStore(s => s.setMessagesNextCursor)
  const messagesNextCursor = useLineChatStore(s => s.messagesNextCursor)
  const messagesLoadingMore = useLineChatStore(s => s.messagesLoadingMore)
  const setMessagesLoadingMore = useLineChatStore(s => s.setMessagesLoadingMore)
  const setConversationsLoading = useLineChatStore(s => s.setConversationsLoading)
  const setMessagesLoading = useLineChatStore(s => s.setMessagesLoading)
  const addOptimisticMessage = useLineChatStore(s => s.addOptimisticMessage)
  const resolveOptimisticMessage = useLineChatStore(s => s.resolveOptimisticMessage)
  const failOptimisticMessage = useLineChatStore(s => s.failOptimisticMessage)
  const removeMessage = useLineChatStore(s => s.removeMessage)
  const updateConversationLastMessage = useLineChatStore(s => s.updateConversationLastMessage)

  const setConversationCustomerId = useLineChatStore(s => s.setConversationCustomerId)

  // Local UI state
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [replyText, setReplyText] = useState('')

  // 顧客連携 state
  const [linkedCustomer, setLinkedCustomer] = useState<Customer | null>(null)
  const [customerLoading, setCustomerLoading] = useState(false)
  const [showCustomerSearch, setShowCustomerSearch] = useState(false)
  const [customerSearchQuery, setCustomerSearchQuery] = useState('')
  const [customerSearchResults, setCustomerSearchResults] = useState<Customer[]>([])
  const [customerSearching, setCustomerSearching] = useState(false)
  const [showCustomerPanel, setShowCustomerPanel] = useState(false)

  // Phase 4 state
  const [operators, setOperators] = useState<Operator[]>([])
  const [showOperatorPicker, setShowOperatorPicker] = useState(false)
  const [templates, setTemplates] = useState<LineTemplate[]>([])
  const [showTemplatePicker, setShowTemplatePicker] = useState(false)
  const [showNotesEditor, setShowNotesEditor] = useState(false)
  const [editingNotes, setEditingNotes] = useState('')
  const [editingTags, setEditingTags] = useState<string[]>([])
  const [newTagInput, setNewTagInput] = useState('')

  // Phase 5.1 filter state
  const [showFilters, setShowFilters] = useState(false)
  const [filterStatus, setFilterStatus] = useState<'all' | 'unhandled' | 'in_progress' | 'done'>('all')
  const [filterOperatorId, setFilterOperatorId] = useState<string>('all')
  const [filterTag, setFilterTag] = useState<string>('')

  // AI assist state
  const [showAiPanel, setShowAiPanel] = useState(false)
  const [aiAvailable, setAiAvailable] = useState(false)
  const [aiMode, setAiMode] = useState<'auto' | 'instruct'>('auto')
  const [aiInstruction, setAiInstruction] = useState('')
  const [aiTone, setAiTone] = useState('')
  const [aiGenerating, setAiGenerating] = useState(false)
  const [aiError, setAiError] = useState('')

  const updateConversationFields = useLineChatStore(s => s.updateConversationFields)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isInitialScrollRef = useRef(true)

  // 選択中のトーク
  const selectedConversation = conversations.find(c => c.lineUserId === selectedLineUserId)

  // 会話リストの仮想スクロール（68px = py-3*2 + 内容44px）
  const CONVERSATION_ITEM_HEIGHT = 68
  const virtualConversations = useVirtualScroll({
    itemCount: conversations.length,
    itemHeight: CONVERSATION_ITEM_HEIGHT,
    overscan: 8,
  })

  // 検索のデバウンス（300ms）
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // フィルタ付きクエリパラメータを構築
  const buildConversationQueryParams = useCallback((extraCursor?: string) => {
    const params = new URLSearchParams()
    params.set('limit', '50')
    if (extraCursor) params.set('cursor', extraCursor)
    if (filterStatus !== 'all') params.set('responseStatus', filterStatus)
    if (filterOperatorId !== 'all') params.set('operatorId', filterOperatorId)
    if (filterTag) params.set('tag', filterTag)
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim())
    return params.toString()
  }, [filterStatus, filterOperatorId, filterTag, debouncedSearch])

  // トーク一覧の取得
  const loadConversations = useCallback(async () => {
    try {
      setConversationsLoading(true)
      const qs = buildConversationQueryParams()
      const data = await api.get<ConversationsResponse>(`/api/line/conversations?${qs}`)
      setConversations(data.conversations)
      setConversationsNextCursor(data.nextCursor ?? null)
    } catch (error) {
      console.error('LINE トーク一覧の取得に失敗:', error)
    } finally {
      setConversationsLoading(false)
    }
  }, [api, buildConversationQueryParams, setConversations, setConversationsLoading, setConversationsNextCursor])

  // トーク一覧の追加読み込み
  const loadMoreConversations = useCallback(async () => {
    if (!conversationsNextCursor || conversationsLoadingMore) return
    try {
      setConversationsLoadingMore(true)
      const qs = buildConversationQueryParams(conversationsNextCursor)
      const data = await api.get<ConversationsResponse>(`/api/line/conversations?${qs}`)
      appendConversations(data.conversations, data.nextCursor ?? null)
    } catch (error) {
      console.error('トーク追加読み込みに失敗:', error)
    } finally {
      setConversationsLoadingMore(false)
    }
  }, [api, buildConversationQueryParams, conversationsNextCursor, conversationsLoadingMore, appendConversations, setConversationsLoadingMore])

  // メッセージ取得（最新50件）
  const loadMessages = useCallback(async (lineUserId: string) => {
    try {
      setMessagesLoading(true)
      const data = await api.get<MessagesResponse>(`/api/line/conversations/${lineUserId}/messages?limit=50`)
      setMessages(data.messages)
      setMessagesNextCursor(data.nextCursor ?? null)
    } catch (error) {
      console.error('メッセージの取得に失敗:', error)
    } finally {
      setMessagesLoading(false)
    }
  }, [api, setMessages, setMessagesLoading, setMessagesNextCursor])

  // 古いメッセージの追加読み込み
  const loadOlderMessages = useCallback(async () => {
    if (!messagesNextCursor || messagesLoadingMore || !selectedLineUserId) return
    const container = messagesContainerRef.current
    const prevScrollHeight = container?.scrollHeight ?? 0
    try {
      setMessagesLoadingMore(true)
      const data = await api.get<MessagesResponse>(`/api/line/conversations/${selectedLineUserId}/messages?limit=50&cursor=${encodeURIComponent(messagesNextCursor)}`)
      prependMessages(data.messages, data.nextCursor ?? null)
      // スクロール位置を維持（古いメッセージが上に追加されるため）
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevScrollHeight
        }
      })
    } catch (error) {
      console.error('メッセージ追加読み込みに失敗:', error)
    } finally {
      setMessagesLoadingMore(false)
    }
  }, [api, selectedLineUserId, messagesNextCursor, messagesLoadingMore, prependMessages, setMessagesLoadingMore])

  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  // ページ離脱時に選択状態をクリア（他ページで incrementUnread がスキップされるのを防ぐ）
  useEffect(() => {
    return () => {
      useLineChatStore.getState().setSelectedLineUserId(null)
    }
  }, [])

  // URLパラメータから会話を自動選択
  useEffect(() => {
    const targetUserId = initialLineUserIdRef.current
    if (targetUserId && !conversationsLoading && conversations.length > 0) {
      initialLineUserIdRef.current = null
      setSelectedLineUserId(targetUserId)
      // URLからパラメータを削除
      setSearchParams({}, { replace: true })
    }
  }, [conversationsLoading, conversations, setSelectedLineUserId, setSearchParams])

  // 署名付きURL期限切れ時にメッセージを再取得（デバウンス付き）
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshMessages = useCallback(() => {
    if (!selectedLineUserId) return
    if (refreshTimerRef.current) return // 既にスケジュール済み
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null
      loadMessages(selectedLineUserId)
    }, 500)
  }, [selectedLineUserId, loadMessages])

  // メディア読み込み失敗による再取得はメッセージごとに 1 回まで。
  // ファイル実体が存在しない壊れたメディアは新しい署名URLでも失敗するため、
  // 上限がないと「再取得 → また失敗 → また再取得」の無限リロードになる
  const refreshedMediaRef = useRef(new Set<string>())
  const handleMediaError = useCallback((messageId: string) => {
    if (refreshedMediaRef.current.has(messageId)) return
    refreshedMediaRef.current.add(messageId)
    refreshMessages()
  }, [refreshMessages])

  // 最下部追従 (pin-to-bottom)。
  // 画像・動画は表示後に読み込まれて高さが伸びるため、一度最下部へスクロール
  // しても位置が上へずれていく。最下部付近にいる間 (= 追従オン) はメディアの
  // 読み込み完了ごとに最下部へ寄せ直す。ユーザーが上へスクロールしたら追従オフ。
  const stickToBottomRef = useRef(true)
  const handleMessagesScroll = useCallback(() => {
    const c = messagesContainerRef.current
    if (!c) return
    stickToBottomRef.current = c.scrollHeight - c.scrollTop - c.clientHeight < 100
  }, [])
  const handleMediaLoaded = useCallback(() => {
    if (stickToBottomRef.current) {
      messagesEndRef.current?.scrollIntoView()
    }
  }, [])

  // トーク選択時にメッセージ取得
  useEffect(() => {
    if (selectedLineUserId) {
      isInitialScrollRef.current = true
      stickToBottomRef.current = true
      refreshedMediaRef.current.clear()
      loadMessages(selectedLineUserId)
    }
  }, [selectedLineUserId, loadMessages])

  // メッセージ表示後に最下部へスクロール。
  // 発火するのは「初回読み込み」と「末尾に新規メッセージが追加されたとき」のみ。
  // 過去メッセージの prepend や署名URL更新の再取得では配列は変わるが末尾は同じなので
  // スクロールしない (発火すると読み込みのたびに上下へ往復してしまう)
  const lastMessageIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (messages.length === 0) {
      isInitialScrollRef.current = true
      lastMessageIdRef.current = null
      return
    }
    const lastId = messages[messages.length - 1].messageId
    if (!isInitialScrollRef.current && lastId === lastMessageIdRef.current) return
    // 初回読み込み時は即座にスクロール、新メッセージ追加時は smooth
    const behavior = isInitialScrollRef.current ? 'instant' as ScrollBehavior : 'smooth' as ScrollBehavior
    isInitialScrollRef.current = false
    lastMessageIdRef.current = lastId
    // DOM 更新後にスクロール
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior })
    })
  }, [messages])

  // トーク選択時に紐付け顧客を取得
  useEffect(() => {
    if (!selectedLineUserId) {
      setLinkedCustomer(null)
      setShowCustomerPanel(false)
      return
    }
    const loadLinkedCustomer = async () => {
      setCustomerLoading(true)
      try {
        const data = await api.get<CustomerResponse>(`/api/line/conversations/${selectedLineUserId}/customer`)
        setLinkedCustomer(data.customer)
      } catch (error) {
        console.error('紐付け顧客の取得に失敗:', error)
        setLinkedCustomer(null)
      } finally {
        setCustomerLoading(false)
      }
    }
    loadLinkedCustomer()
  }, [selectedLineUserId, api])

  // 顧客検索
  const handleCustomerSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setCustomerSearchResults([])
      return
    }
    setCustomerSearching(true)
    try {
      const data = await api.get<CustomerSearchResponse>(`/api/customers?search=${encodeURIComponent(query)}&limit=10`)
      setCustomerSearchResults(data.customers ?? data.items ?? [])
    } catch (error) {
      console.error('顧客検索に失敗:', error)
    } finally {
      setCustomerSearching(false)
    }
  }, [api])

  // 顧客紐付け
  const handleLinkCustomer = async (customerId: string) => {
    if (!selectedLineUserId) return
    try {
      const data = await api.put<CustomerResponse>(`/api/line/conversations/${selectedLineUserId}/customer`, { customerId })
      setLinkedCustomer(data.customer)
      setConversationCustomerId(selectedLineUserId, customerId)
      setShowCustomerSearch(false)
      setCustomerSearchQuery('')
      setCustomerSearchResults([])
    } catch (error) {
      console.error('顧客紐付けに失敗:', error)
    }
  }

  // 顧客紐付け解除
  const handleUnlinkCustomer = async () => {
    if (!selectedLineUserId) return
    try {
      await api.delete(`/api/line/conversations/${selectedLineUserId}/customer`)
      setLinkedCustomer(null)
      setConversationCustomerId(selectedLineUserId, undefined)
      setShowCustomerPanel(false)
    } catch (error) {
      console.error('顧客紐付け解除に失敗:', error)
    }
  }

  // オペレーター一覧の取得（初回のみ）
  useEffect(() => {
    const loadOperators = async () => {
      try {
        const data = await api.get<UsersResponse>('/api/users')
        setOperators(data.items ?? [])
      } catch (error) {
        console.error('オペレーター取得に失敗:', error)
      }
    }
    loadOperators()
  }, [api])

  // テンプレート一覧の取得（初回のみ）
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const data = await api.get<TemplatesResponse>('/api/line/templates')
        setTemplates(data.templates ?? [])
      } catch (error) {
        console.error('テンプレート取得に失敗:', error)
      }
    }
    loadTemplates()
  }, [api])

  // AI設定の有無を確認
  useEffect(() => {
    const checkAiConfig = async () => {
      try {
        const data = await api.get<AiConfigResponse>('/api/line/ai-config')
        setAiAvailable(!!data.aiConfig?.apiKey)
      } catch {
        setAiAvailable(false)
      }
    }
    checkAiConfig()
  }, [api])

  // AI返信生成
  const handleAiGenerate = async () => {
    if (!selectedLineUserId) return
    setAiGenerating(true)
    setAiError('')
    try {
      const body: Record<string, string> = {
        lineUserId: selectedLineUserId,
        mode: aiMode,
      }
      if (aiMode === 'instruct' && aiInstruction.trim()) {
        body.instruction = aiInstruction.trim()
      }
      if (aiTone.trim()) {
        body.tone = aiTone.trim()
      }
      const data = await api.post<AiGenerateResponse>('/api/line/ai/generate-reply', body)
      setReplyText(data.generatedText)
      setShowAiPanel(false)
      setAiInstruction('')
    } catch (error: unknown) {
      setAiError(error instanceof Error ? error.message : 'AI返信の生成に失敗しました')
    } finally {
      setAiGenerating(false)
    }
  }

  // ピン留めトグル
  const handleTogglePin = async (lineUserId: string) => {
    const conv = conversations.find(c => c.lineUserId === lineUserId)
    if (!conv) return
    const isPinned = !!conv.pinnedAt
    try {
      await api.put(`/api/line/conversations/${lineUserId}`, { pinned: !isPinned })
      updateConversationFields(lineUserId, {
        pinnedAt: isPinned ? undefined : new Date().toISOString(),
      })
    } catch (error) {
      console.error('ピン留め変更に失敗:', error)
    }
  }

  // オペレーター割り当て
  const handleAssignOperator = async (operatorId: string | null) => {
    if (!selectedLineUserId) return
    try {
      await api.put(`/api/line/conversations/${selectedLineUserId}`, {
        assignedOperatorId: operatorId,
      })
      updateConversationFields(selectedLineUserId, { assignedOperatorId: operatorId ?? undefined })
      setShowOperatorPicker(false)
    } catch (error) {
      console.error('オペレーター割り当てに失敗:', error)
    }
  }

  // 対応ステータス変更
  const handleStatusChange = async (status: 'unhandled' | 'in_progress' | 'done') => {
    if (!selectedLineUserId) return
    try {
      await api.put(`/api/line/conversations/${selectedLineUserId}`, {
        responseStatus: status,
      })
      updateConversationFields(selectedLineUserId, { responseStatus: status })
    } catch (error) {
      console.error('ステータス変更に失敗:', error)
    }
  }

  // メモ保存
  const handleSaveNotes = async () => {
    if (!selectedLineUserId) return
    try {
      await api.put(`/api/line/conversations/${selectedLineUserId}`, {
        notes: editingNotes || null,
      })
      updateConversationFields(selectedLineUserId, { notes: editingNotes || undefined })
      setShowNotesEditor(false)
    } catch (error) {
      console.error('メモ保存に失敗:', error)
    }
  }

  // タグ追加
  const handleAddTag = async (tag: string) => {
    if (!selectedLineUserId || !tag.trim()) return
    const conv = conversations.find(c => c.lineUserId === selectedLineUserId)
    const currentTags = conv?.tags ?? []
    if (currentTags.includes(tag.trim())) return
    const newTags = [...currentTags, tag.trim()]
    try {
      await api.put(`/api/line/conversations/${selectedLineUserId}`, { tags: newTags })
      updateConversationFields(selectedLineUserId, { tags: newTags })
      setNewTagInput('')
    } catch (error) {
      console.error('タグ追加に失敗:', error)
    }
  }

  // タグ削除
  const handleRemoveTag = async (tag: string) => {
    if (!selectedLineUserId) return
    const conv = conversations.find(c => c.lineUserId === selectedLineUserId)
    const newTags = (conv?.tags ?? []).filter(t => t !== tag)
    try {
      await api.put(`/api/line/conversations/${selectedLineUserId}`, { tags: newTags })
      updateConversationFields(selectedLineUserId, { tags: newTags })
    } catch (error) {
      console.error('タグ削除に失敗:', error)
    }
  }

  // テンプレートをメッセージ入力に挿入
  const handleInsertTemplate = (content: string) => {
    setReplyText(prev => prev + content)
    setShowTemplatePicker(false)
  }

  // メッセージ送信（楽観的更新）
  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedLineUserId) return

    const text = replyText.trim()
    const tempId = crypto.randomUUID()
    const now = new Date().toISOString()

    // 楽観的にメッセージを追加
    const optimisticMsg: LineMessage = {
      organizationId: '',
      messageId: tempId,
      lineUserId: selectedLineUserId,
      direction: 'outgoing',
      type: 'text',
      text,
      createdAt: now,
      _sendStatus: 'sending',
      _tempId: tempId,
    }
    addOptimisticMessage(optimisticMsg)
    setReplyText('')

    try {
      const result = await api.post<SendMessageResponse>(`/api/line/conversations/${selectedLineUserId}/messages`, { text })
      // 仮IDを実IDに差し替え
      resolveOptimisticMessage(tempId, {
        organizationId: '',
        messageId: result.messageId,
        lineUserId: selectedLineUserId,
        direction: 'outgoing',
        type: 'text',
        text,
        createdAt: result.createdAt,
      })
      updateConversationLastMessage(selectedLineUserId, text, result.createdAt)
    } catch (error) {
      console.error('メッセージの送信に失敗:', error)
      failOptimisticMessage(tempId)
    }
  }

  // 失敗メッセージの再送
  const handleRetry = async (msg: LineMessage) => {
    if (!msg._tempId) return
    const tempId = msg._tempId
    const lineUserId = msg.lineUserId

    // 送信中に戻す
    const store = useLineChatStore.getState()
    store.resolveOptimisticMessage(tempId, { ...msg, _sendStatus: 'sending', _tempId: tempId })

    try {
      const result = await api.post<SendMessageResponse>(`/api/line/conversations/${lineUserId}/messages`, { text: msg.text })
      resolveOptimisticMessage(tempId, {
        organizationId: '',
        messageId: result.messageId,
        lineUserId,
        direction: 'outgoing',
        type: 'text',
        text: msg.text,
        createdAt: result.createdAt,
      })
      updateConversationLastMessage(lineUserId, msg.text ?? '', result.createdAt)
    } catch (error) {
      console.error('メッセージの再送に失敗:', error)
      failOptimisticMessage(tempId)
    }
  }

  // 画像送信
  const handleImageSend = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedLineUserId) return
    // reset input
    e.target.value = ''

    const tempId = crypto.randomUUID()
    const now = new Date().toISOString()
    const previewUrl = URL.createObjectURL(file)

    // 楽観的に画像メッセージを追加
    const optimisticMsg: LineMessage = {
      organizationId: '',
      messageId: tempId,
      lineUserId: selectedLineUserId,
      direction: 'outgoing',
      type: 'image',
      mediaUrl: previewUrl,
      createdAt: now,
      _sendStatus: 'sending',
      _tempId: tempId,
    }
    addOptimisticMessage(optimisticMsg)

    try {
      const formData = new FormData()
      formData.append('image', file)
      const result = await api.uploadFile<ImageUploadResponse>(`/api/line/conversations/${selectedLineUserId}/images`, formData)
      resolveOptimisticMessage(tempId, {
        organizationId: '',
        messageId: result.messageId,
        lineUserId: selectedLineUserId,
        direction: 'outgoing',
        type: 'image',
        mediaUrl: result.mediaUrl,
        createdAt: result.createdAt,
      })
      updateConversationLastMessage(selectedLineUserId, '[画像]', result.createdAt)
    } catch (error) {
      console.error('画像の送信に失敗:', error)
      failOptimisticMessage(tempId)
    } finally {
      URL.revokeObjectURL(previewUrl)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      handleSendReply()
    }
  }

  // フィルタはサーバーサイドで適用済み — conversations をそのまま使用
  const filteredConversations = conversations

  // タグ一覧（フィルタ用）— ロード済みの会話から収集
  const allTags = [...new Set(conversations.flatMap(c => c.tags ?? []))].sort()

  // アクティブフィルタ数
  const activeFilterCount = [filterStatus !== 'all', filterOperatorId !== 'all', filterTag !== ''].filter(Boolean).length

  /** LINE スタンプ画像URL */
  const getStickerUrl = (stickerId: string) =>
    `https://stickershop.line-scdn.net/stickershop/v1/sticker/${stickerId}/android/sticker.png`

  /** ファイルサイズの表示 */
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  /** 秒数 → mm:ss */
  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  /** タイムスタンプ + 送信ステータスのフッター */
  const renderTimestamp = (msg: LineMessage, isOutgoing: boolean) => (
    <div className={`flex items-center gap-1 mt-1 ${
      isOutgoing
        ? msg._sendStatus === 'failed' ? 'text-red-500' : 'text-green-100'
        : 'text-gray-400 dark:text-gray-500'
    }`}>
      {msg._sendStatus === 'sending' && (
        <div className="animate-spin w-3 h-3 border border-green-100 border-t-transparent rounded-full" />
      )}
      {msg._sendStatus === 'failed' && (
        <button
          onClick={() => handleRetry(msg)}
          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 dark:text-red-400"
        >
          <AlertCircle className="w-3 h-3" />
          <span>再送</span>
          <RefreshCw className="w-3 h-3" />
        </button>
      )}
      {!msg._sendStatus && (
        <span className="text-xs">{formatTime(msg.createdAt)}</span>
      )}
    </div>
  )

  /** メッセージ吹き出しの描画 */
  const renderMessageBubble = (msg: LineMessage, isOutgoing: boolean) => {
    // スタンプ
    if (msg.type === 'sticker' && msg.stickerId) {
      return (
        <div className="flex flex-col items-center">
          <img
            src={getStickerUrl(msg.stickerId)}
            alt="スタンプ"
            className="w-24 h-24 object-contain"
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.style.display = 'none'
              e.currentTarget.nextElementSibling?.classList.remove('hidden')
            }}
          />
          <span className="hidden text-xs text-gray-400">[スタンプ]</span>
          <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {formatTime(msg.createdAt)}
          </span>
        </div>
      )
    }

    // 画像
    if (msg.type === 'image' && msg.mediaUrl) {
      return (
        <div className="max-w-[70%]">
          <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer">
            <img
              src={msg.mediaUrl}
              alt="画像"
              className="rounded-xl max-h-64 object-contain cursor-pointer hover:opacity-90 transition-opacity"
              onError={() => handleMediaError(msg.messageId)}
              onLoad={handleMediaLoaded}
            />
          </a>
          <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 block">
            {formatTime(msg.createdAt)}
          </span>
        </div>
      )
    }

    // 動画
    if (msg.type === 'video' && msg.mediaUrl) {
      return (
        <div className="max-w-[70%]">
          <video
            src={msg.mediaUrl}
            controls
            preload="metadata"
            className="rounded-xl max-h-64"
            onError={() => handleMediaError(msg.messageId)}
            onLoadedMetadata={handleMediaLoaded}
          />
          <div className="flex items-center gap-2 mt-1">
            {msg.duration != null && (
              <span className="text-xs text-gray-400 dark:text-gray-500">{formatDuration(msg.duration)}</span>
            )}
            <span className="text-xs text-gray-400 dark:text-gray-500">{formatTime(msg.createdAt)}</span>
          </div>
        </div>
      )
    }

    // 音声
    if (msg.type === 'audio' && msg.mediaUrl) {
      return (
        <div className={`max-w-[70%] px-4 py-2 rounded-2xl ${
          isOutgoing ? 'bg-green-500 rounded-br-md' : 'bg-gray-100 dark:bg-gray-700 rounded-bl-md'
        }`}>
          <audio src={msg.mediaUrl} controls preload="metadata" className="max-w-full h-8" onError={() => handleMediaError(msg.messageId)} onLoadedMetadata={handleMediaLoaded} />
          <div className="flex items-center gap-2 mt-1">
            {msg.duration != null && (
              <span className={`text-xs ${isOutgoing ? 'text-green-100' : 'text-gray-400 dark:text-gray-500'}`}>
                {formatDuration(msg.duration)}
              </span>
            )}
            <span className={`text-xs ${isOutgoing ? 'text-green-100' : 'text-gray-400 dark:text-gray-500'}`}>
              {formatTime(msg.createdAt)}
            </span>
          </div>
        </div>
      )
    }

    // 位置情報
    if (msg.type === 'location' && msg.latitude != null && msg.longitude != null) {
      const mapsUrl = `https://www.google.com/maps?q=${msg.latitude},${msg.longitude}`
      return (
        <div className={`max-w-[70%] px-4 py-3 rounded-2xl ${
          isOutgoing ? 'bg-green-500 text-white rounded-br-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-md'
        }`}>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 hover:opacity-80 transition-opacity">
            <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              {msg.text && <p className="text-sm font-medium">{msg.text}</p>}
              {msg.address && <p className="text-xs opacity-80 mt-0.5">{msg.address}</p>}
              <p className="text-xs opacity-60 mt-0.5">{msg.latitude.toFixed(6)}, {msg.longitude.toFixed(6)}</p>
            </div>
          </a>
          <span className={`text-xs mt-1 block ${isOutgoing ? 'text-green-100' : 'text-gray-400 dark:text-gray-500'}`}>
            {formatTime(msg.createdAt)}
          </span>
        </div>
      )
    }

    // ファイル
    if (msg.type === 'file' && msg.mediaUrl) {
      return (
        <div className={`max-w-[70%] px-4 py-3 rounded-2xl ${
          isOutgoing ? 'bg-green-500 text-white rounded-br-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-md'
        }`}>
          <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer" download className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <FileText className="w-5 h-5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{msg.fileName ?? 'ファイル'}</p>
              {msg.fileSize != null && (
                <p className="text-xs opacity-70">{formatFileSize(msg.fileSize)}</p>
              )}
            </div>
            <Download className="w-4 h-4 flex-shrink-0 opacity-70" />
          </a>
          <span className={`text-xs mt-1 block ${isOutgoing ? 'text-green-100' : 'text-gray-400 dark:text-gray-500'}`}>
            {formatTime(msg.createdAt)}
          </span>
        </div>
      )
    }

    // 連絡先
    if (msg.type === 'contact' && msg.contactDisplayName) {
      return (
        <div className={`max-w-[70%] px-4 py-3 rounded-2xl ${
          isOutgoing ? 'bg-green-500 text-white rounded-br-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-md'
        }`}>
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium">{msg.contactDisplayName}</p>
              <p className="text-xs opacity-60">連絡先</p>
            </div>
          </div>
          <span className={`text-xs mt-1 block ${isOutgoing ? 'text-green-100' : 'text-gray-400 dark:text-gray-500'}`}>
            {formatTime(msg.createdAt)}
          </span>
        </div>
      )
    }

    // テキストメッセージ（デフォルト）
    return (
      <div
        className={`max-w-[70%] px-4 py-2 rounded-2xl ${
          isOutgoing
            ? msg._sendStatus === 'failed'
              ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-br-md'
              : 'bg-green-500 text-white rounded-br-md'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-md'
        }`}
      >
        <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
        {renderTimestamp(msg, isOutgoing)}
      </div>
    )
  }

  const formatTime = (timestamp: string | null | undefined) => {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    if (isToday) {
      return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
    }
    return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="flex h-[calc(100vh-140px)] bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* ===== 左パネル: トーク一覧 ===== */}
      <div className={`w-80 border-r border-gray-200 dark:border-gray-700 flex flex-col flex-shrink-0 ${
        selectedLineUserId ? 'hidden md:flex' : 'flex'
      }`}>
        {/* ヘッダー */}
        <div className="px-4 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">LINE チャット</h2>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="トークを検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 border-0 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white"
            />
          </div>
          {/* フィルタトグル */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`mt-2 flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-colors w-full ${
              activeFilterCount > 0
                ? 'text-green-600 bg-green-50 dark:bg-green-900/20'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Filter className="w-3 h-3" />
            フィルタ
            {activeFilterCount > 0 && (
              <span className="ml-1 w-4 h-4 bg-green-500 text-white text-[10px] rounded-full flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* フィルタパネル */}
        {showFilters && (
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 space-y-2 bg-gray-50 dark:bg-gray-900/30">
            {/* ステータス */}
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider">ステータス</label>
              <div className="flex gap-1 mt-0.5">
                {([['all', 'すべて'], ['unhandled', '未対応'], ['in_progress', '対応中'], ['done', '完了']] as const).map(([val, label]) => (
                  <button
                    key={val}
                    onClick={() => setFilterStatus(val)}
                    className={`px-2 py-0.5 text-xs rounded transition-colors ${
                      filterStatus === val
                        ? 'bg-green-500 text-white'
                        : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {/* 担当者 */}
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wider">担当者</label>
              <select
                value={filterOperatorId}
                onChange={(e) => setFilterOperatorId(e.target.value)}
                className="w-full mt-0.5 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 dark:text-white"
              >
                <option value="all">すべて</option>
                <option value="unassigned">未割り当て</option>
                {operators.map(op => (
                  <option key={op.userId} value={op.userId}>{op.displayName ?? op.name}</option>
                ))}
              </select>
            </div>
            {/* タグ */}
            {allTags.length > 0 && (
              <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-wider">タグ</label>
                <select
                  value={filterTag}
                  onChange={(e) => setFilterTag(e.target.value)}
                  className="w-full mt-0.5 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 dark:text-white"
                >
                  <option value="">すべて</option>
                  {allTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
            )}
            {/* フィルタクリア */}
            {activeFilterCount > 0 && (
              <button
                onClick={() => { setFilterStatus('all'); setFilterOperatorId('all'); setFilterTag('') }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                フィルタをクリア
              </button>
            )}
          </div>
        )}

        {/* トーク一覧（仮想スクロール） */}
        <div ref={virtualConversations.containerRef} className="flex-1 overflow-y-auto"
          onScroll={(e) => {
            // 末尾付近で自動追加読み込み
            const el = e.currentTarget
            if (conversationsNextCursor && !conversationsLoadingMore && el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
              loadMoreConversations()
            }
          }}
        >
          {conversationsLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-400 dark:text-gray-500">
              <MessageCircle className="w-8 h-8 mb-2" />
              <p className="text-sm">トークがありません</p>
            </div>
          ) : (
            <div style={virtualConversations.innerStyle}>
              {Array.from(
                { length: virtualConversations.visibleRange.end - virtualConversations.visibleRange.start },
                (_, i) => virtualConversations.visibleRange.start + i
              ).map((index) => {
                const conv = filteredConversations[index]
                if (!conv) return null
                // ピン留め区切り線: 前のアイテムがpinned、現在がunpinnedの場合
                const prevConv = index > 0 ? filteredConversations[index - 1] : null
                const showPinSeparator = prevConv?.pinnedAt && !conv.pinnedAt
                return (
                  <button
                    key={conv.lineUserId}
                    style={virtualConversations.getItemStyle(index)}
                    onClick={() => setSelectedLineUserId(conv.lineUserId)}
                    className={`group w-full px-4 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      selectedLineUserId === conv.lineUserId
                        ? 'bg-green-50 dark:bg-green-900/20 border-l-2 border-green-500'
                        : ''
                    } ${showPinSeparator ? 'border-t-2 border-yellow-200 dark:border-yellow-800' : ''}`}
                  >
                    {/* アバター */}
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {conv.pictureUrl ? (
                        <img src={conv.pictureUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-5 h-5 text-white" />
                      )}
                    </div>
                    {/* 名前・最新メッセージ */}
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {conv.displayName || 'LINE ユーザー'}
                        </span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2">
                          {formatTime(conv.lastMessageAt)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate flex-1">
                          {conv.lastMessage || 'メッセージなし'}
                        </p>
                        {(conv.responseStatus === 'unhandled' || !conv.responseStatus) && (
                          <CircleDot className="w-3 h-3 text-red-400 flex-shrink-0" />
                        )}
                        {conv.responseStatus === 'in_progress' && (
                          <Clock className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                        )}
                        {conv.responseStatus === 'done' && (
                          <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                    {/* ピン留めアイコン（クリックでトグル） */}
                    <span
                      role="button"
                      onClick={(e) => { e.stopPropagation(); handleTogglePin(conv.lineUserId) }}
                      className={`flex-shrink-0 p-0.5 rounded transition-opacity ${
                        conv.pinnedAt
                          ? 'opacity-100 text-yellow-500'
                          : 'opacity-0 group-hover:opacity-60 text-gray-400 hover:text-yellow-500'
                      }`}
                      title={conv.pinnedAt ? 'ピン留め解除' : 'ピン留め'}
                    >
                      <Pin className={`w-3.5 h-3.5 ${conv.pinnedAt ? 'fill-yellow-500' : ''}`} />
                    </span>
                    {/* 未読バッジ */}
                    {conv.unreadCount > 0 && (
                      <span className="w-5 h-5 bg-green-500 text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                        {conv.unreadCount}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
          {/* 追加読み込みインジケータ */}
          {conversationsLoadingMore && (
            <div className="flex items-center justify-center py-2">
              <div className="animate-spin w-4 h-4 border border-green-500 border-t-transparent rounded-full" />
            </div>
          )}
        </div>
      </div>

      {/* ===== 右パネル: メッセージ ===== */}
      <div className={`flex-1 flex flex-col ${
        !selectedLineUserId ? 'hidden md:flex' : 'flex'
      }`}>
        {selectedConversation ? (
          <>
            {/* メッセージヘッダー */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
              <button
                onClick={() => setSelectedLineUserId(null)}
                className="md:hidden p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center overflow-hidden">
                {selectedConversation.pictureUrl ? (
                  <img src={selectedConversation.pictureUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-4 h-4 text-white" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {selectedConversation.displayName || 'LINE ユーザー'}
                </h3>
                {linkedCustomer ? (
                  <button
                    onClick={() => setShowCustomerPanel(!showCustomerPanel)}
                    className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1"
                  >
                    <Building className="w-3 h-3" />
                    <span className="truncate">{linkedCustomer.name}{linkedCustomer.companyName ? ` / ${linkedCustomer.companyName}` : ''}</span>
                  </button>
                ) : (
                  <p className="text-xs text-gray-400">LINE</p>
                )}
              </div>
              {/* ピン留め + 顧客連携ボタン */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleTogglePin(selectedConversation.lineUserId)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    selectedConversation.pinnedAt
                      ? 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                      : 'text-gray-400 hover:text-yellow-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title={selectedConversation.pinnedAt ? 'ピン留め解除' : 'ピン留め'}
                >
                  <Pin className={`w-4 h-4 ${selectedConversation.pinnedAt ? 'fill-yellow-500' : ''}`} />
                </button>
                {linkedCustomer ? (
                  <>
                    <button
                      onClick={() => setShowCustomerPanel(!showCustomerPanel)}
                      className={`p-1.5 rounded-lg transition-colors ${showCustomerPanel ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30' : 'text-gray-400 hover:text-blue-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      title="顧客情報を表示"
                    >
                      <User className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleUnlinkCustomer}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="顧客紐付け解除"
                    >
                      <Unlink className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setShowCustomerSearch(true)}
                    className="p-1.5 text-gray-400 hover:text-green-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="顧客を紐付け"
                  >
                    <Link className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* 顧客情報パネル（紐付け済みの場合） */}
            {showCustomerPanel && linkedCustomer && (
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/10">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{linkedCustomer.name}</h4>
                    {linkedCustomer.priority === 'vip' && (
                      <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    )}
                    {linkedCustomer.priority === 'high' && (
                      <Star className="w-4 h-4 text-orange-500" />
                    )}
                  </div>
                  <button onClick={() => setShowCustomerPanel(false)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {linkedCustomer.companyName && (
                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                      <Building className="w-3 h-3" />
                      <span className="truncate">{linkedCustomer.companyName}</span>
                    </div>
                  )}
                  {linkedCustomer.phoneNumber && (
                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                      <Phone className="w-3 h-3" />
                      <span>{formatPhoneNumberForDisplay(linkedCustomer.phoneNumber)}</span>
                    </div>
                  )}
                  {linkedCustomer.email && (
                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400 col-span-2">
                      <span className="truncate">{linkedCustomer.email}</span>
                    </div>
                  )}
                </div>
                {linkedCustomer.tags && linkedCustomer.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {linkedCustomer.tags.map((tag: string) => (
                      <span key={tag} className="px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {linkedCustomer.notes && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">{linkedCustomer.notes}</p>
                )}
              </div>
            )}

            {/* 顧客検索モーダル */}
            {showCustomerSearch && (
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">顧客を検索して紐付け</h4>
                  <button onClick={() => { setShowCustomerSearch(false); setCustomerSearchQuery(''); setCustomerSearchResults([]) }} className="ml-auto p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="名前、電話番号、会社名で検索..."
                    value={customerSearchQuery}
                    onChange={(e) => {
                      setCustomerSearchQuery(e.target.value)
                      handleCustomerSearch(e.target.value)
                    }}
                    autoFocus
                    className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:text-white"
                  />
                </div>
                {customerSearching && (
                  <div className="flex items-center justify-center py-3">
                    <div className="animate-spin w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full" />
                  </div>
                )}
                {customerSearchResults.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                    {customerSearchResults.map((c) => (
                      <button
                        key={c.customerId}
                        onClick={() => handleLinkCustomer(c.customerId)}
                        className="w-full px-3 py-2 text-left bg-white dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900 dark:text-white">{c.name}</span>
                          {c.priority === 'vip' && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {c.companyName && <span>{c.companyName}</span>}
                          {c.phoneNumber && <span>{formatPhoneNumberForDisplay(c.phoneNumber)}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {customerSearchQuery && !customerSearching && customerSearchResults.length === 0 && (
                  <p className="text-xs text-gray-400 mt-2 text-center">該当する顧客が見つかりません</p>
                )}
              </div>
            )}

            {/* 運用バー: ステータス、担当者、タグ、メモ */}
            <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 flex-wrap text-xs">
              {/* 対応ステータス */}
              <div className="flex items-center gap-1">
                {(['unhandled', 'in_progress', 'done'] as const).map((s) => {
                  const labels = { unhandled: '未対応', in_progress: '対応中', done: '完了' }
                  const icons = { unhandled: CircleDot, in_progress: Clock, done: Check }
                  const colors = {
                    unhandled: 'text-red-500 bg-red-50 dark:bg-red-900/20',
                    in_progress: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20',
                    done: 'text-green-600 bg-green-50 dark:bg-green-900/20',
                  }
                  const Icon = icons[s]
                  const isActive = (selectedConversation?.responseStatus ?? 'unhandled') === s
                  return (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(s)}
                      className={`px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                        isActive ? colors[s] + ' font-semibold' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {labels[s]}
                    </button>
                  )
                })}
              </div>

              <span className="text-gray-300 dark:text-gray-600">|</span>

              {/* 担当者 */}
              <div className="relative">
                <button
                  onClick={() => setShowOperatorPicker(!showOperatorPicker)}
                  className="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-400"
                >
                  <UserCheck className="w-3 h-3" />
                  {selectedConversation?.assignedOperatorId
                    ? operators.find(o => o.userId === selectedConversation.assignedOperatorId)?.displayName ?? '担当者'
                    : '未割り当て'}
                </button>
                {showOperatorPicker && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10">
                    <button
                      onClick={() => handleAssignOperator(null)}
                      className="w-full px-3 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500"
                    >
                      未割り当てにする
                    </button>
                    {operators.map((op) => (
                      <button
                        key={op.userId}
                        onClick={() => handleAssignOperator(op.userId)}
                        className={`w-full px-3 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 ${
                          selectedConversation?.assignedOperatorId === op.userId ? 'text-green-600 font-semibold' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span>{op.displayName ?? op.name}</span>
                        {selectedConversation?.assignedOperatorId === op.userId && <Check className="w-3 h-3" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <span className="text-gray-300 dark:text-gray-600">|</span>

              {/* タグ */}
              <div className="flex items-center gap-1 flex-wrap">
                <Tag className="w-3 h-3 text-gray-400" />
                {(selectedConversation?.tags ?? []).map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-500">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
                <form
                  onSubmit={(e) => { e.preventDefault(); handleAddTag(newTagInput) }}
                  className="inline-flex"
                >
                  <input
                    type="text"
                    value={newTagInput}
                    onChange={(e) => setNewTagInput(e.target.value)}
                    placeholder="+ タグ"
                    className="w-16 px-1 py-0.5 text-xs bg-transparent border-b border-gray-300 dark:border-gray-600 focus:border-green-500 outline-none dark:text-white"
                  />
                </form>
              </div>

              <span className="text-gray-300 dark:text-gray-600">|</span>

              {/* メモ */}
              <button
                onClick={() => {
                  setEditingNotes(selectedConversation?.notes ?? '')
                  setShowNotesEditor(!showNotesEditor)
                }}
                className={`flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                  selectedConversation?.notes ? 'text-blue-600' : 'text-gray-400'
                }`}
              >
                <StickyNote className="w-3 h-3" />
                メモ
              </button>
            </div>

            {/* メモ編集エリア */}
            {showNotesEditor && (
              <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-yellow-50 dark:bg-yellow-900/10">
                <textarea
                  value={editingNotes}
                  onChange={(e) => setEditingNotes(e.target.value)}
                  placeholder="メモを入力..."
                  rows={3}
                  className="w-full text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 dark:text-white resize-none"
                />
                <div className="flex justify-end gap-2 mt-1">
                  <button
                    onClick={() => setShowNotesEditor(false)}
                    className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    キャンセル
                  </button>
                  <button
                    onClick={handleSaveNotes}
                    className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                  >
                    保存
                  </button>
                </div>
              </div>
            )}

            {/* メッセージ一覧 */}
            <div ref={messagesContainerRef} onScroll={handleMessagesScroll} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messagesLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <MessageCircle className="w-12 h-12 mb-3" />
                  <p>メッセージがありません</p>
                </div>
              ) : (
                <>
                {/* 古いメッセージ読み込みボタン */}
                {messagesNextCursor && (
                  <div className="flex justify-center pb-2">
                    <button
                      onClick={loadOlderMessages}
                      disabled={messagesLoadingMore}
                      className="px-3 py-1 text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-full hover:bg-green-100 dark:hover:bg-green-900/40"
                    >
                      {messagesLoadingMore ? (
                        <span className="flex items-center gap-1">
                          <div className="animate-spin w-3 h-3 border border-green-500 border-t-transparent rounded-full" />
                          読み込み中...
                        </span>
                      ) : (
                        '過去のメッセージを表示'
                      )}
                    </button>
                  </div>
                )}
                {messages.map((msg) => {
                  const isOutgoing = msg.direction === 'outgoing'
                  return (
                    <div
                      key={msg._tempId ?? msg.messageId}
                      className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}
                    >
                      {renderMessageBubble(msg, isOutgoing)}
                    </div>
                  )
                })}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* テンプレート選択パネル */}
            {showTemplatePicker && (
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 max-h-48 overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">テンプレート</span>
                  <button onClick={() => setShowTemplatePicker(false)} className="p-0.5 text-gray-400 hover:text-gray-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                {templates.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-2">テンプレートがありません（設定画面から追加）</p>
                ) : (
                  <div className="space-y-1">
                    {templates.map((t) => (
                      <button
                        key={t.templateId}
                        onClick={() => handleInsertTemplate(t.content)}
                        className="w-full px-3 py-2 text-left bg-white dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg border border-gray-200 dark:border-gray-700 transition-colors"
                      >
                        <span className="text-xs font-medium text-gray-900 dark:text-white">{t.title}</span>
                        {t.category && <span className="ml-2 text-xs text-gray-400">[{t.category}]</span>}
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{t.content}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* AI返信アシストパネル */}
            {showAiPanel && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-purple-50/50 dark:bg-purple-900/10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    <span className="text-xs font-semibold text-purple-700 dark:text-purple-300">AI返信アシスト</span>
                  </div>
                  <button onClick={() => setShowAiPanel(false)} className="p-0.5 text-gray-400 hover:text-gray-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* モード切替 */}
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setAiMode('auto')}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      aiMode === 'auto'
                        ? 'bg-purple-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    自動生成
                  </button>
                  <button
                    onClick={() => setAiMode('instruct')}
                    className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      aiMode === 'instruct'
                        ? 'bg-purple-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    指示入力
                  </button>
                </div>

                {/* 指示入力（instructモード時） */}
                {aiMode === 'instruct' && (
                  <textarea
                    value={aiInstruction}
                    onChange={(e) => setAiInstruction(e.target.value)}
                    rows={2}
                    placeholder="例: 商品の在庫がないことを丁寧にお伝えして、入荷予定を案内する"
                    className="w-full px-3 py-2 mb-3 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-purple-500 dark:text-white resize-none"
                  />
                )}

                {/* トーン */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">トーン:</span>
                  <div className="flex gap-1">
                    {['丁寧', 'カジュアル', 'ビジネス'].map((tone) => (
                      <button
                        key={tone}
                        onClick={() => setAiTone(aiTone === tone ? '' : tone)}
                        className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                          aiTone === tone
                            ? 'bg-purple-500 text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-purple-300'
                        }`}
                      >
                        {tone}
                      </button>
                    ))}
                  </div>
                </div>

                {/* エラー表示 */}
                {aiError && (
                  <div className="mb-3 px-3 py-2 text-xs text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 rounded-lg">
                    {aiError}
                  </div>
                )}

                {/* 生成ボタン */}
                <button
                  onClick={handleAiGenerate}
                  disabled={aiGenerating || (aiMode === 'instruct' && !aiInstruction.trim())}
                  className="w-full px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {aiGenerating ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      生成中...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3.5 h-3.5" />
                      返信を生成
                    </>
                  )}
                </button>
              </div>
            )}

            {/* 返信入力 */}
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-end gap-2">
                {/* 画像アップロード */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSend}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-gray-400 hover:text-green-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                  title="画像を送信"
                >
                  <ImageIcon className="w-5 h-5" />
                </button>
                {/* テンプレート */}
                <button
                  onClick={() => { setShowTemplatePicker(!showTemplatePicker); setShowAiPanel(false) }}
                  className={`p-2 rounded-xl transition-colors ${
                    showTemplatePicker
                      ? 'text-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'text-gray-400 hover:text-green-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title="テンプレート"
                >
                  <BookOpen className="w-5 h-5" />
                </button>
                {/* AI返信ボタン */}
                {aiAvailable && (
                  <button
                    onClick={() => { setShowAiPanel(!showAiPanel); setShowTemplatePicker(false) }}
                    className={`p-2 rounded-xl transition-colors ${
                      showAiPanel
                        ? 'text-purple-500 bg-purple-50 dark:bg-purple-900/20'
                        : 'text-gray-400 hover:text-purple-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title="AI返信アシスト"
                  >
                    <Sparkles className="w-5 h-5" />
                  </button>
                )}
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="メッセージを入力..."
                  rows={3}
                  className="flex-1 resize-y min-h-[2.5rem] max-h-48 px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-green-500 dark:text-white"
                />
                <button
                  onClick={handleSendReply}
                  disabled={!replyText.trim()}
                  className="p-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-xl transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          /* 未選択時の空状態 */
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
              <MessageCircle className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300 mb-1">LINE チャット</h3>
            <p className="text-sm">左のトークを選択してメッセージを表示</p>
          </div>
        )}
      </div>
    </div>
  )
}
